import type { ArtifactKind, GateMode, NodeKind } from '@api/processes';
import type {
  NodeDefinitionDto,
  PortDefinitionDto,
  PortGroupDefinitionDto,
} from '@api/nodeDefinitions';
import {
  makeId,
  TASK_AUTO_SKILL_ID,
  TASK_DEFAULT_LLM_MODEL,
  type Port,
  type ProcessNode,
  type ProcessNodeData,
} from './graphTypes';
import { stringList } from './taskSkillSelection';
import { LLM_MODEL_AUTO } from '@utils/llmModel';

/**
 * The MIME type used to carry node-creation intent across HTML drag-and-drop.
 * The NodeLibrary attaches the palette type via `dataTransfer.setData` and the
 * GraphCanvas reads it back on `onDrop`. Kept as a constant so both ends
 * agree without typo risk.
 */
export const PALETTE_DRAG_MIME = 'application/x-aibuild-node-type';

/**
 * Companion MIME carrying the palette item's user-facing label (e.g.
 * "CAD convert", "Pymeshlab"). The drop handler passes it through as the
 * fresh node's default label so validation messages and the node card's
 * editable LABEL field both read meaningfully out of the box, instead of
 * the generic "Task". Optional — when missing (older payloads, programmatic
 * drops), the catalog-derived default is used.
 */
export const PALETTE_DRAG_LABEL_MIME = 'application/x-aibuild-node-label';

/**
 * `globalThis` CustomEvent dispatched by the editor's collapsed rail
 * when the user CLICKS (not drags) a quick-add button. Click-to-add
 * lives at the global level for the same reason the prototype's
 * `aibuild-open-templates` event does: the rail (in the sidebar slot)
 * and the GraphCanvas (in the main slot) live in sibling subtrees with
 * no shared parent — and React Flow's `screenToFlowPosition` is only
 * available from inside the `ReactFlowProvider`, so the canvas has to
 * be the one that owns the placement logic. A custom event lets the
 * rail say "user wants this palette type" without knowing anything
 * about the canvas's internal coordinate system.
 *
 * Detail shape: `{ paletteType: PaletteType, label: string }` — the
 * same data the drag flow puts in `dataTransfer`, repackaged for the
 * synchronous click-to-add path.
 */
export const PALETTE_ADD_EVENT = 'aibuild:add-palette-node';

export interface PaletteAddEventDetail {
  paletteType: string;
  label: string;
}

/**
 * Fire a click-to-add request. Centralised here so consumers can't
 * accidentally use the wrong event name string. Returns the dispatched
 * event so callers can `event.preventDefault()` if needed (e.g. tests).
 */
export const dispatchPaletteAddEvent = (
  detail: PaletteAddEventDetail,
): CustomEvent<PaletteAddEventDetail> => {
  const event = new CustomEvent<PaletteAddEventDetail>(PALETTE_ADD_EVENT, {
    detail,
  });
  globalThis.dispatchEvent(event);
  return event;
};

/**
 * The strings the NodeLibrary dispatches. Structural types are bare ids
 * (`input`, `gate`, ...); agent-bound types carry the agent id after a
 * colon. Kept open as `string` because the agent registry isn't fixed yet —
 * `paletteTypeToNode` parses defensively and ignores anything it doesn't
 * recognise.
 */
export type PaletteType =
  | 'input'
  | 'output'
  | 'gate'
  | 'note'
  | 'image_gen'
  | '3d_gen'
  | 'manual'
  | 'task'
  | `task:${string}`;

interface PaletteDropOptions {
  /** Position in flow coordinates where the new node should land. */
  position: { x: number; y: number };
  /**
   * The node-definition catalog (server source of truth). Required —
   * defaults are catalog-driven so a freshly dropped node mirrors the
   * backend agent schema exactly. Pass the data from
   * {@code useNodeDefinitions().data}.
   */
  definitions: readonly NodeDefinitionDto[];
  /**
   * Optional default label for the freshly-created node. When present,
   * overrides the catalog-derived default. The NodeLibrary drag flow
   * sets this to the palette item's display label.
   */
  defaultLabel?: string;
  /**
   * Optional id factory — exposed so tests can pass a deterministic stub.
   * Defaults to a UUIDv4.
   */
  idFactory?: () => string;
}

// Fresh palette-dropped nodes haven't been computed yet, so the run
// snapshot is empty across the board.
const FRESH_RUN_SNAPSHOT = {
  latestExecution: null,
  latestArtifacts: [] as const,
  isRunnable: false,
} as const;

/**
 * Resolve the catalog entry for a palette drop. Tasks resolve by
 * skillId (image_gen / 3d_gen are their own definition ids); the
 * structural kinds resolve by id == kind. Returns {@code undefined} if
 * the catalog hasn't loaded the matching definition — caller treats
 * that as a no-op rather than fabricating a default.
 */
function resolveDefinition(
  catalog: readonly NodeDefinitionDto[],
  kind: NodeKind,
  skillId: string | undefined,
): NodeDefinitionDto | undefined {
  if (kind === 'task') {
    const trimmed = skillId?.trim();
    if (trimmed && trimmed !== 'task') {
      const bySkill = catalog.find(
        (d) => d.kind === 'task' && d.id === trimmed,
      );
      if (bySkill) return bySkill;
    }
    return catalog.find((d) => d.id === 'task');
  }
  return catalog.find((d) => d.id === kind);
}

/**
 * Read a port's accepted-kinds set from the catalog DTO with a safe
 * fallback. Backwards-compatible — older catalog payloads that omit
 * {@code acceptedKinds} fall back to a singleton of {@code portType}.
 */
const acceptedKindsOf = (port: PortDefinitionDto): ArtifactKind[] => {
  if (port.acceptedKinds && port.acceptedKinds.length > 0) {
    return [...port.acceptedKinds];
  }
  return [port.portType];
};

/**
 * Render a single port row from a {@link PortGroupDefinitionDto}
 * instance. Substitutes a 1-based instance index into the {@code "{n}"}
 * placeholder of {@code labelTemplate} when present; otherwise uses the
 * template verbatim (Task's "Output" group has no {@code "{n}"} because
 * the user renames each port).
 */
const labelForGroupInstance = (
  group: PortGroupDefinitionDto,
  instanceIdx: number,
): string => group.labelTemplate.replace('{n}', String(instanceIdx + 1));

/**
 * Materialise a fresh {@link Port} array directly from the catalog's
 * {@code defaultPorts}. Each port gets a fresh UUID; positions are
 * direction-relative array indices; {@code acceptedKinds} carries the
 * multi-type set (singleton for group-materialised ports).
 *
 * <p>Both static ports ({@code inputs}/{@code outputs}) and variable
 * groups ({@code inputGroups}/{@code outputGroups}) are materialised:
 * group ports come AFTER the static slots and contribute
 * {@code defaultCount} ports each, keyed off the group's {@code type}.
 * Today only Task uses an output group (1 port, MODEL_3D); the input
 * group path is wired through for forward consistency.</p>
 */
function portsFromDefinition(
  definition: NodeDefinitionDto,
  idFactory: () => string,
): Port[] {
  const ports: Port[] = [];
  let inputPosition = 0;
  definition.defaultPorts.inputs.forEach((p) => {
    ports.push({
      portId: idFactory(),
      direction: 'input',
      label: p.label,
      portType: p.portType,
      acceptedKinds: acceptedKindsOf(p),
      position: inputPosition++,
    });
  });
  definition.defaultPorts.inputGroups.forEach((group) => {
    for (let i = 0; i < group.defaultCount; i++) {
      ports.push({
        portId: idFactory(),
        direction: 'input',
        label: labelForGroupInstance(group, i),
        portType: group.type,
        acceptedKinds: [group.type],
        position: inputPosition++,
      });
    }
  });
  let outputPosition = 0;
  definition.defaultPorts.outputs.forEach((p) => {
    ports.push({
      portId: idFactory(),
      direction: 'output',
      label: p.label,
      portType: p.portType,
      acceptedKinds: acceptedKindsOf(p),
      position: outputPosition++,
    });
  });
  definition.defaultPorts.outputGroups.forEach((group) => {
    for (let i = 0; i < group.defaultCount; i++) {
      ports.push({
        portId: idFactory(),
        direction: 'output',
        label: labelForGroupInstance(group, i),
        portType: group.type,
        acceptedKinds: [group.type],
        position: outputPosition++,
      });
    }
  });
  return ports;
}

/**
 * Read a string config value with an empty-string fallback. Pulled out
 * so the four call-sites in {@link defaultDataFromCatalog} stay terse
 * and the function's cognitive complexity stays under SonarJS's limit.
 */
const stringConfigOr = (
  config: Record<string, unknown>,
  key: string,
  fallback = '',
): string => (typeof config[key] === 'string' ? (config[key] as string) : fallback);

/** Auto last: a drop the author has not steered yet is what Auto is for, and it
 *  keeps a quick-add node runnable. */
const resolveTaskSkillIds = (
  definition: NodeDefinitionDto,
  skillId: string | undefined,
  defaultConfig: Record<string, unknown>,
): string[] => {
  // 'task' is the generic definition id, never a real skill — treat it
  // as "no skill picked" rather than seeding the unselectable literal.
  const explicit = skillId?.trim();
  if (explicit && explicit !== 'task') return [explicit];
  const fromList = stringList(defaultConfig.skillIds);
  if (fromList.length > 0) return fromList;
  const fromConfig =
    (stringConfigOr(defaultConfig, 'skillId') || stringConfigOr(defaultConfig, 'agentId')).trim();
  if (fromConfig) return [fromConfig];
  if (definition.kind === 'task' && definition.id !== 'task') {
    return [definition.id];
  }
  return [TASK_AUTO_SKILL_ID];
};

/**
 * Materialise a fresh {@link ProcessNodeData} from a catalog
 * {@link NodeDefinitionDto}. The catalog is the single source of truth
 * — defaults come from {@code definition.defaultConfig},
 * {@code definition.defaultPorts}, and
 * {@code definition.displayMetadata.label}. No per-kind defaults
 * baked into the FE.
 *
 * <p>Casts go through {@code unknown}: the catalog-driven
 * {@code defaultConfig} spread is intentionally untyped (forward-compat
 * for new wire fields) and the resulting index signature confuses
 * {@link ProcessNodeData}'s discriminated-union narrowing. The runtime
 * shape is correct — driven entirely by the catalog — but the
 * structural-cast bypass is required to keep the function generic
 * across every kind without a per-kind type wrapper.</p>
 */
export function defaultDataFromCatalog(
  definition: NodeDefinitionDto,
  skillId: string | undefined,
  defaultLabel: string | undefined,
  idFactory: () => string,
): ProcessNodeData {
  const label = defaultLabel?.trim() || definition.displayMetadata.label;
  const defaultConfig = (definition.defaultConfig ?? {}) as Record<
    string,
    unknown
  >;

  if (definition.kind === 'note') {
    return { text: stringConfigOr(defaultConfig, 'text') };
  }

  // Spread `defaultConfig` so any forward-compat fields the backend
  // ships (e.g. a future `description` field) round-trip without
  // needing per-kind FE wiring. Then layer the typed-shape fields on
  // top so the renderer can read them flat.
  const base = {
    ...defaultConfig,
    label,
    ports: portsFromDefinition(definition, idFactory),
    ...FRESH_RUN_SNAPSHOT,
  };

  if (definition.kind === 'task') {
    return {
      ...base,
      skillIds: resolveTaskSkillIds(definition, skillId, defaultConfig),
      llmModel: stringConfigOr(defaultConfig, 'llmModel', TASK_DEFAULT_LLM_MODEL),
      instruction: stringConfigOr(defaultConfig, 'instruction'),
      // A freshly dropped task runs in the cloud with no assignee; the
      // detail panel flips these when the author picks LOCAL.
      runnerTarget: defaultConfig.runnerTarget === 'LOCAL' ? 'LOCAL' : 'CLOUD',
      assigneeType: defaultConfig.assigneeType === 'GROUP' ? 'GROUP' : 'USER',
      assigneeIds: stringList(defaultConfig.assigneeIds),
    };
  }
  // Thin-wrapper task definitions (image_gen / 3d_gen) take their
  // user-editable prompt through {@code node.instruction} (mirrors the
  // wire shape — instruction is a first-class column on
  // {@code WriteProcessNodeDto}, not buried in config). Seed it to ''
  // so the freshly-dropped node renders an editable textarea instead
  // of an undefined value crashing the controlled-input.
  if (definition.kind === 'image_gen' || definition.kind === '3d_gen') {
    return {
      ...base,
      instruction: stringConfigOr(defaultConfig, 'instruction'),
    };
  }
  if (definition.kind === 'gate') {
    // Seed `mode` from the catalog's defaultConfig (defaults to MANUAL
    // server-side) so a freshly dropped gate immediately shows the
    // correct mode-specific config fields. Without this seed the
    // value would round-trip through `coerceGateData`'s fallback on
    // the next save and the AUTO/MANUAL select would briefly render
    // empty.
    const seedMode: GateMode =
      defaultConfig.mode === 'AUTO' ? 'AUTO' : 'MANUAL';
    // {@code maxIterations} comes from the catalog's defaultConfig
    // via the {@code ...base} spread above. The backend's
    // {@code GateConfig.@JsonCreator} guarantees it is always
    // present (and clamped into 1..10) on the API response, so the
    // FE just trusts the wire value here -- no per-field default
    // seeding, no coercion. If the backend changes the contract,
    // {@code coerceGateData} (the read-side path) is the right
    // place to compensate, not the palette drop.
    return {
      ...base,
      mode: seedMode,
      reviewerIds: stringList(defaultConfig.reviewerIds),
      reviewInstructions: stringConfigOr(defaultConfig, 'reviewInstructions'),
      // Preselect "Auto" so a freshly dropped gate shows the model
      // dropdown with the sentinel chosen rather than an empty
      // placeholder. Mirrors the Task node's llmModel seed above; the
      // coerce read-path (coerceGateData) folds the same default.
      llmModel: stringConfigOr(defaultConfig, 'llmModel', LLM_MODEL_AUTO),
    } as unknown as ProcessNodeData;
  }
  if (definition.kind === 'manual') {
    // A freshly dropped manual node has no assignee yet; the detail
    // panel's people/group pickers fill these in. Normalise the id list
    // to an array so the picker renders a controlled value.
    return {
      ...base,
      assigneeType: defaultConfig.assigneeType === 'GROUP' ? 'GROUP' : 'USER',
      assigneeIds: stringList(defaultConfig.assigneeIds),
      instructions: stringConfigOr(defaultConfig, 'instructions'),
    } as unknown as ProcessNodeData;
  }
  // The `note` short-circuit above already handles the only kind that
  // needs a shape other than label/ports/runSnapshot.
  return base as unknown as ProcessNodeData;
}

/**
 * Parse a NodeLibrary palette string into the structural node type +
 * optional skill binding. Returns `null` for unknown strings — caller should
 * treat that as a no-op (not crash).
 */
export function parsePaletteType(
  raw: string | null | undefined,
): { kind: NodeKind; skillId?: string } | null {
  if (!raw) return null;
  if (
    raw === 'input' ||
    raw === 'output' ||
    raw === 'gate' ||
    raw === 'note' ||
    raw === 'image_gen' ||
    raw === '3d_gen' ||
    raw === 'manual'
  ) {
    return { kind: raw };
  }
  if (raw === 'task' || raw.startsWith('task:')) {
    const suffix = raw.startsWith('task:') ? raw.slice('task:'.length).trim() : '';
    const skillId = suffix && suffix !== '_' ? suffix : undefined;
    return { kind: 'task', skillId };
  }
  return null;
}

/**
 * Materialise a `ProcessNode` from a palette string and the catalog.
 * Every freshly-created node carries the `node-card__drag-handle`
 * selector so React Flow knows to only respond to drags that start in
 * the card header — body fields stay editable without accidentally
 * panning the node away.
 *
 * Returns `null` when the palette string is unknown or when the catalog
 * doesn't (yet) carry the matching definition. Callers should treat
 * that as a no-op.
 */
export function paletteTypeToNode(
  rawType: string | null | undefined,
  { position, definitions, defaultLabel, idFactory = makeId }: PaletteDropOptions,
): ProcessNode | null {
  const parsed = parsePaletteType(rawType);
  if (!parsed) return null;
  const definition = resolveDefinition(definitions, parsed.kind, parsed.skillId);
  if (!definition) return null;
  const id = idFactory();
  return {
    id,
    type: parsed.kind,
    position,
    data: defaultDataFromCatalog(definition, parsed.skillId, defaultLabel, idFactory),
    dragHandle: '.node-card__drag-handle',
  };
}
